import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://api.vgo.global';
const getToken = () => localStorage.getItem('vgo_token') || localStorage.getItem('token');

const STATUS_CONFIG = {
  escrow:     { label: 'Escrow',      bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  pending:    { label: 'Pending',     bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' },
  confirmed:  { label: 'Confirmed',   bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
  accepted:   { label: 'Accepted',    bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
  completed:  { label: 'Completed',   bg: '#f0fdf4', color: '#166534', dot: '#22c55e' },
  released:   { label: 'Released',    bg: '#f0fdf4', color: '#166534', dot: '#22c55e' },
  cancelled:  { label: 'Cancelled',   bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
  refunded:   { label: 'Refunded',    bg: '#faf5ff', color: '#6b21a8', dot: '#a855f7' },
  processing: { label: 'Processing',  bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' },
  shipped:    { label: 'Shipped',     bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
  delivered:  { label: 'Delivered',   bg: '#f0fdf4', color: '#166534', dot: '#22c55e' },
  packing:    { label: 'Packing',     bg: '#fff7ed', color: '#9a3412', dot: '#f97316' },
};

const TABS = [
  { id: 'all',      label: 'All',      icon: '📋' },
  { id: 'services', label: 'Services', icon: '🛎️' },
  { id: 'shop',     label: 'Shop',     icon: '🛍️' },
];

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status?.toLowerCase()] || { label: status || 'Unknown', bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:cfg.bg, color:cfg.color, borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700, letterSpacing:0.3 }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:cfg.dot, display:'inline-block' }} />
      {cfg.label}
    </span>
  );
}

function OrderCard({ order, type, onTap }) {
  const isService = type === 'service';
  const title    = isService ? (order.service || order.service_name || order.service_title || 'Service Booking') : (order.product_name || order.title || order.item_summary || 'Shop Order');
  const subtitle = isService ? (order.provider || order.provider_name || order.category_name || '') : (order.seller_name || order.shop_name || '');
  const amount   = order.amount || order.total_amount || order.price || 0;
  const currency = isService ? 'TXC' : (order.currency || order.token || 'TXC');
  const date     = order.created_at || order.placed_at || order.booking_date;
  const status   = order.status || 'pending';
  const orderId  = order.id || order.booking_id || order.order_id || '';
  const formattedDate = date ? new Date(date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';

  return (
    <div onClick={() => onTap && onTap(order, type)} style={{ background:'#fff', borderRadius:16, padding:'14px 16px', marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.07)', cursor:'pointer', border:'1px solid #f1f5f9' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div style={{ flex:1, marginRight:8 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:2, lineHeight:1.3 }}>{title}</div>
          {subtitle ? <div style={{ fontSize:12, color:'#64748b' }}>{subtitle}</div> : null}
        </div>
        <StatusBadge status={status} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:10, fontWeight:600, color: isService ? '#1a3cc8' : '#7c3aed', background: isService ? '#eff6ff' : '#f5f3ff', borderRadius:8, padding:'2px 7px' }}>
            {isService ? '🛎️ Service' : '🛍️ Shop'}
          </span>
          {formattedDate && <span style={{ fontSize:11, color:'#94a3b8' }}>{formattedDate}</span>}
        </div>
        <div style={{ fontSize:15, fontWeight:800, color:'#1a3cc8' }}>{Number(amount).toLocaleString()} {currency}</div>
      </div>
      {orderId && <div style={{ marginTop:6, fontSize:10, color:'#cbd5e1', fontFamily:'monospace' }}>#{String(orderId).slice(0,18)}…</div>}
    </div>
  );
}

function EmptyState({ tab }) {
  const msgs = {
    all:      { icon:'📭', text:'No orders yet',        sub:'Your orders will appear here once you place them.' },
    services: { icon:'🛎️', text:'No service bookings', sub:'Book a doctor, beauty, lab test and more.' },
    shop:     { icon:'🛍️', text:'No shop orders',      sub:'Browse and order from our shops.' },
  };
  const m = msgs[tab] || msgs.all;
  return (
    <div style={{ textAlign:'center', padding:'60px 24px' }}>
      <div style={{ fontSize:56, marginBottom:14 }}>{m.icon}</div>
      <div style={{ fontSize:17, fontWeight:700, color:'#1e293b', marginBottom:6 }}>{m.text}</div>
      <div style={{ fontSize:13, color:'#94a3b8', lineHeight:1.5 }}>{m.sub}</div>
    </div>
  );
}

export default function OrderHistoryScreen() {
  const nav = useNavigate();
  const [activeTab,     setActiveTab]     = useState('all');
  const [serviceOrders, setServiceOrders] = useState([]);
  const [shopOrders,    setShopOrders]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [error,         setError]         = useState(null);

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    const headers = { 'Content-Type':'application/json', Authorization:`Bearer ${getToken()}` };
    try {
      const [svcRes, shopRes] = await Promise.allSettled([
        fetch(`${API}/v1/bookings/my`,  { headers }),
        fetch(`${API}/v1/shop/orders`,  { headers }),
      ]);
      if (svcRes.status  === 'fulfilled' && svcRes.value.ok)  { const d = await svcRes.value.json();  setServiceOrders(Array.isArray(d) ? d : (d.bookings || d.data || [])); }
      if (shopRes.status === 'fulfilled' && shopRes.value.ok) { const d = await shopRes.value.json(); setShopOrders(Array.isArray(d) ? d : d.orders  || d.data || []); }
    } catch (e) {
      setError('Could not load orders. Check your connection.');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const allOrders = [
    ...serviceOrders.map(o => ({ ...o, _type:'service' })),
    ...shopOrders.map(o    => ({ ...o, _type:'shop'    })),
  ].sort((a, b) => new Date(b.created_at || b.placed_at || 0) - new Date(a.created_at || a.placed_at || 0));

  const displayOrders =
    activeTab === 'all'      ? allOrders :
    activeTab === 'services' ? serviceOrders.map(o => ({ ...o, _type:'service' })) :
    shopOrders.map(o => ({ ...o, _type:'shop' }));

  const activeSvc  = serviceOrders.filter(o => ['escrow','pending','confirmed','accepted'].includes(o.status)).length;
  const activeShop = shopOrders.filter(o =>    ['processing','packing','shipped'].includes(o.status)).length;

  const handleOrderTap = (order, type) => {
    if (type === 'service') nav(`/booking/${order.id || order.booking_id}`);
    else nav(`/shop/orders/${order.id || order.order_id}`);
  };

  return (
    <div style={{ maxWidth:430, margin:'0 auto', background:'#f8fafc', minHeight:'100vh', paddingBottom:80 }}>

      {/* HEADER */}
      <div style={{ background:'linear-gradient(135deg, #1a3cc8 0%, #2d4fd6 100%)', padding:'52px 16px 20px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
          <button onClick={() => nav(-1)} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#fff', fontSize:18 }}>‹</button>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:'#fff', letterSpacing:-0.5 }}>Order History</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.65)', marginTop:1 }}>{allOrders.length} total orders</div>
          </div>
          <button onClick={() => fetchAll(true)} disabled={refreshing} style={{ marginLeft:'auto', background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, cursor:'pointer', color:'#fff', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', opacity: refreshing ? 0.5 : 1 }}>
            {refreshing ? '⏳' : '🔄'}
          </button>
        </div>
        {(activeSvc + activeShop) > 0 && (
          <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:12, padding:'10px 14px', display:'flex', gap:16 }}>
            {activeSvc  > 0 && <div style={{ color:'#fff', fontSize:12 }}><span style={{ fontWeight:800, fontSize:18 }}>{activeSvc}</span><span style={{ marginLeft:4, opacity:0.8 }}>active booking{activeSvc > 1 ? 's' : ''}</span></div>}
            {activeShop > 0 && <div style={{ color:'#fff', fontSize:12 }}><span style={{ fontWeight:800, fontSize:18 }}>{activeShop}</span><span style={{ marginLeft:4, opacity:0.8 }}>order{activeShop > 1 ? 's' : ''} in progress</span></div>}
          </div>
        )}
      </div>

      {/* TABS */}
      <div style={{ display:'flex', gap:8, padding:'14px 16px 6px', background:'#fff', borderBottom:'1px solid #f1f5f9', position:'sticky', top:120, zIndex:90 }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex:1, padding:'8px 4px', border:'none', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:12, background: activeTab === tab.id ? '#1a3cc8' : '#f1f5f9', color: activeTab === tab.id ? '#fff' : '#64748b' }}>
            {tab.icon} {tab.label}
            {tab.id === 'services' && serviceOrders.length > 0 && <span style={{ marginLeft:4, background: activeTab==='services' ? 'rgba(255,255,255,0.3)' : '#e2e8f0', borderRadius:10, padding:'1px 5px', fontSize:10 }}>{serviceOrders.length}</span>}
            {tab.id === 'shop'     && shopOrders.length    > 0 && <span style={{ marginLeft:4, background: activeTab==='shop'     ? 'rgba(255,255,255,0.3)' : '#e2e8f0', borderRadius:10, padding:'1px 5px', fontSize:10 }}>{shopOrders.length}</span>}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ padding:'12px 16px' }}>
        {loading ? (
          <div style={{ padding:'60px 0', textAlign:'center' }}>
            <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
            <div style={{ color:'#94a3b8', fontSize:14 }}>Loading your orders…</div>
          </div>
        ) : error ? (
          <div style={{ padding:'40px 16px', textAlign:'center' }}>
            <div style={{ fontSize:36, marginBottom:10 }}>⚠️</div>
            <div style={{ color:'#ef4444', fontSize:14, marginBottom:16 }}>{error}</div>
            <button onClick={() => fetchAll()} style={{ background:'#1a3cc8', color:'#fff', border:'none', borderRadius:10, padding:'10px 24px', fontSize:14, fontWeight:700, cursor:'pointer' }}>Try Again</button>
          </div>
        ) : displayOrders.length === 0 ? (
          <EmptyState tab={activeTab} />
        ) : (
          displayOrders.map((order, idx) => (
            <OrderCard key={order.id || order.booking_id || idx} order={order} type={order._type} onTap={handleOrderTap} />
          ))
        )}
      </div>
    </div>
  );
}
